import { createContext, useCallback, useContext, useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { EN, type Catalog, type CatalogKey, type LocaleCatalog, type PluralBase } from './catalog.js';

/**
 * Internationalisation.
 *
 * English is the official language and the only complete catalogue: every
 * other locale is a `LocaleCatalog`, a partial map checked against the English
 * keys, so a key nobody has translated yet falls back to English rather than
 * rendering its own name. That is what keeps a half-finished translation from
 * being worse than no translation.
 *
 * Three things this does that a naive `t()` does not:
 *
 *  - **Plurals through `Intl.PluralRules`.** Russian has one/few/many and
 *    Arabic has six categories; a `count === 1 ? a : b` would be wrong in both.
 *    A key may declare `_one`, `_two`, `_few`, `_many`, `_other` variants and
 *    the right one is chosen by the platform's own rules for that locale.
 *  - **Numbers and dates through `Intl`.** A Russian reader expects `1 234,50`
 *    and an Indonesian `1.234,50`; hardcoding `toLocaleString('en-US')` makes
 *    a translated screen read like a machine.
 *  - **Direction.** Arabic sets `dir="rtl"` on the document, which is what
 *    makes the layout mirror rather than merely the words change.
 *
 * Money is deliberately *not* localised: an amount of Gems is formatted by
 * `@pxc/shared`'s `formatMpxc` on both sides of the wire, and a balance that
 * renders differently per locale is a support ticket waiting to happen.
 */

export const LOCALES = ['en', 'es', 'ar', 'ru', 'pt', 'id'] as const;
export type Locale = (typeof LOCALES)[number];

/** What the language picker shows — each language in its own language. */
export const LOCALE_NAMES: Record<Locale, string> = {
  en: 'English',
  es: 'Español',
  ar: 'العربية',
  ru: 'Русский',
  pt: 'Português',
  id: 'Bahasa Indonesia',
};

/** The only right-to-left language here. */
const RTL_LOCALES = new Set<Locale>(['ar']);

export function isRtl(locale: Locale): boolean {
  return RTL_LOCALES.has(locale);
}

/**
 * The catalogues in hand, and how the others arrive.
 *
 * English is bundled, because it is the fallback for every key in every
 * language and something has to render on the first frame. The other five are
 * fetched on demand: together they are about a hundred kilobytes of text, and
 * shipping six languages to every user so that each one can read one of them
 * is a hundred kilobytes spent to deliver twenty.
 *
 * Until the chosen one has arrived, `lookup` falls through to English — which
 * is the same path a missing key takes, so the first frame in Arabic is a
 * frame of English rather than a blank screen or a spinner. It is replaced the
 * moment the import resolves, and Vite emits each locale as its own chunk.
 */
const CATALOGS: Partial<Record<Locale, LocaleCatalog>> = { en: EN };

const LOADERS: Record<Exclude<Locale, 'en'>, () => Promise<{ default?: LocaleCatalog } & Record<string, unknown>>> = {
  es: () => import('./locales/es.js'),
  ar: () => import('./locales/ar.js'),
  ru: () => import('./locales/ru.js'),
  pt: () => import('./locales/pt.js'),
  id: () => import('./locales/id.js'),
};

/** Each locale is fetched once, however many components ask for it. */
const inFlight = new Map<Locale, Promise<void>>();

/**
 * Fetches a locale's catalogue.
 *
 * Exported as `preloadLocale` so the boot sequence can warm it while the
 * splash is already waiting on the artwork — which is what removes the single
 * frame of English an Arabic reader would otherwise see. Resolves either way:
 * a locale that will not load leaves the app in English rather than blocking
 * the boot on it.
 */
export function loadCatalog(locale: Locale): Promise<void> {
  if (locale === 'en' || CATALOGS[locale]) return Promise.resolve();
  const existing = inFlight.get(locale);
  if (existing) return existing;

  const promise = LOADERS[locale]()
    .then((module) => {
      // Each file exports one uppercase constant named after the locale.
      const catalog = module[locale.toUpperCase()] as LocaleCatalog | undefined;
      if (catalog) CATALOGS[locale] = catalog;
    })
    .catch(() => {
      // A locale that will not load leaves the app in English rather than
      // broken. Nothing is retried: the next launch tries again anyway.
    });
  inFlight.set(locale, promise);
  return promise;
}

/** BCP 47 tags for `Intl`, which does not know our short codes for everything. */
const INTL_TAGS: Record<Locale, string> = {
  en: 'en',
  es: 'es',
  ar: 'ar',
  ru: 'ru',
  pt: 'pt',
  id: 'id',
};

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

/**
 * Best match for what Telegram says the user's language is.
 *
 * Telegram sends a full tag (`pt-BR`, `es-419`, `ar-SA`), so the region is
 * dropped and the base language looked up. Anything we do not have becomes
 * English rather than nothing.
 */
export function resolveLocale(languageCode: string | null | undefined): Locale {
  if (!languageCode) return 'en';
  const base = languageCode.toLowerCase().split(/[-_]/)[0];
  return isLocale(base) ? base : 'en';
}

export type Values = Record<string, string | number>;

/**
 * Looks up one key, falling back to English and then to the key itself.
 *
 * Returning the key is the last resort and is deliberately visible: a screen
 * showing `wallet.balance` is a bug report, whereas a screen showing an empty
 * space is a bug nobody notices.
 */
function lookup(locale: Locale, key: string): string {
  const own = CATALOGS[locale]?.[key as CatalogKey];
  if (typeof own === 'string' && own.length > 0) return own;
  const english = EN[key as CatalogKey];
  return typeof english === 'string' ? english : key;
}

/** `{name}` → the value, leaving an unknown placeholder alone rather than blank. */
function interpolate(template: string, values: Values | undefined): string {
  if (!values) return template;
  return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
    name in values ? String(values[name]) : whole,
  );
}

export interface Translator {
  /** One string, with `{placeholders}` filled in. */
  t(key: CatalogKey, values?: Values): string;
  /**
   * A key built at runtime, or null when neither this locale nor English has
   * it.
   *
   * For lookups keyed by something the server sent — an error code, an asset
   * symbol — where the key cannot be a literal and a missing one must be
   * distinguishable from a present one rather than rendering as itself.
   */
  maybe(key: string, values?: Values): string | null;
  /**
   * One string chosen by count.
   *
   * `key` is the *base* name of a plural family — `combo.attemptsLeft`, not
   * `combo.attemptsLeft_one` — because which variant is right is a runtime
   * question for `Intl.PluralRules`, not a choice the caller makes.
   * `{count}` is always available to the template without being passed.
   */
  plural(key: PluralBase, count: number, values?: Values): string;
  locale: Locale;
  setLocale(locale: Locale): void;
  rtl: boolean;
  /** A number in the reader's own conventions. */
  number(value: number, options?: Intl.NumberFormatOptions): string;
  /** A date in the reader's own conventions. */
  date(value: number | Date, options?: Intl.DateTimeFormatOptions): string;
  /**
   * How much to shrink this string so it still fits the slot it was measured
   * into, as a multiplier between MIN_FIT and 1.
   *
   * The screens are painted artwork: a label sits in a slot whose position and
   * font size were measured off the picture, for the English words, with
   * `white-space: nowrap` and no width to overflow. "Wallet" fits the plate;
   * "Billetera" is half again as long and runs into the icon beside it.
   *
   * English is 1 by construction — the artwork was cut for it — so this can
   * never change the original. Anything longer is scaled by the ratio of the
   * two lengths, which is the same arithmetic SwapSheet already did by hand
   * for the balance ("twenty characters take the plate at full size").
   */
  fit(key: CatalogKey, values?: Values): number;
  /**
   * `fit` and `t` together, as props to spread onto the slot:
   *
   *   <span className="w2-title w2-at" {...at('wallet.title')} />
   */
  at(key: CatalogKey, values?: Values): { children: string; style: Record<string, string> };
}

/**
 * How small a label may get before it is worse than the overflow.
 *
 * Past roughly two thirds the words stop reading as part of the artwork. A
 * translation that still does not fit at the floor is one to shorten in the
 * catalogue rather than to shrink further.
 */
const MIN_FIT = 0.62;

/**
 * `--fit` for a string set into the room a painted word left behind.
 *
 * `at()` measures against English, because English is what most of the artwork
 * was cut for. The daily combo and the roulette were painted in Spanish, so the
 * room their words left is counted in characters instead: `budget` is how many
 * fit at full size, taken from the painted word. A longer translation is shrunk
 * in proportion, down to MIN_FIT; a string with line breaks is measured by its
 * longest line.
 */
export function fitWithin(text: string, budget: number): CSSProperties {
  const longest = Math.max(0, ...text.split('\n').map((line) => [...line].length));
  const ratio = longest <= budget ? 1 : Math.max(MIN_FIT, budget / longest);
  return { '--fit': String(ratio) } as CSSProperties;
}

const I18nContext = createContext<Translator | null>(null);

const STORAGE_KEY = 'pxc.locale';

/**
 * Remembers the choice for the next launch.
 *
 * The authority is the account — `settings.language` on the server, which the
 * Profile screen writes — and this is only so the very first paint after a
 * reload is in the right language instead of flashing English while `/profile`
 * is in flight. Storage can throw or come back empty in a private window, so
 * every access is guarded and the app renders correctly without it.
 */
function readStoredLocale(): Locale | null {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isLocale(stored) ? stored : null;
  } catch {
    return null;
  }
}

function writeStoredLocale(locale: Locale): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // A per-viewer convenience; the account setting is the real record.
  }
}

export function I18nProvider({
  children,
  initialLocale,
}: {
  children: ReactNode;
  /** Usually what Telegram reported, before the account setting has arrived. */
  initialLocale?: Locale;
}) {
  const [locale, setLocaleState] = useState<Locale>(() => readStoredLocale() ?? initialLocale ?? 'en');
  /*
   * Bumped when a fetched catalogue lands, which is what re-renders the tree
   * with the new words in it. The catalogues live in a module-level map rather
   * than in state because they are immutable and shared by every provider —
   * this is only the signal that one arrived.
   */
  const [loaded, setLoaded] = useState(0);

  useEffect(() => {
    if (CATALOGS[locale]) return;
    let cancelled = false;
    void loadCatalog(locale).then(() => {
      if (!cancelled) setLoaded((value) => value + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [locale]);

  /*
   * `dir` and `lang` on the document, not on a wrapper.
   *
   * `dir` is what mirrors the layout: every `margin-inline-start`, every
   * logical padding, every flex row order follows it. `lang` is what lets the
   * browser hyphenate and read the page correctly. Both belong on <html>.
   */
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('lang', INTL_TAGS[locale]);
    root.setAttribute('dir', isRtl(locale) ? 'rtl' : 'ltr');
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    // Started before the state change so the fetch and the render race in the
    // right order: an already-loaded locale switches within one frame.
    void loadCatalog(next);
    setLocaleState(next);
    writeStoredLocale(next);
  }, []);

  const value = useMemo<Translator>(() => {
    const tag = INTL_TAGS[locale];
    // Built once per locale rather than per call: constructing an Intl
    // formatter is not free and these are used on every rendered row.
    const pluralRules = new Intl.PluralRules(tag);
    const numberFormat = new Intl.NumberFormat(tag);

    const t = (key: CatalogKey, values?: Values): string =>
      interpolate(lookup(locale, key), values);

    return {
      locale,
      setLocale,
      rtl: isRtl(locale),
      t,
      maybe: (key, values) => {
        const own = CATALOGS[locale]?.[key as CatalogKey];
        if (typeof own === 'string' && own.length > 0) return interpolate(own, values);
        const english = EN[key as CatalogKey];
        return typeof english === 'string' ? interpolate(english, values) : null;
      },
      plural: (key, count, values) => {
        const category = pluralRules.select(count);
        const merged = { count, ...values };
        // The exact category, then `other`, then the bare key — so a locale
        // that only bothered with one form still reads correctly.
        for (const candidate of [`${key}_${category}`, `${key}_other`, key]) {
          const own = CATALOGS[locale]?.[candidate as CatalogKey];
          if (typeof own === 'string' && own.length > 0) return interpolate(own, merged);
        }
        for (const candidate of [`${key}_${category}`, `${key}_other`, key]) {
          const english = EN[candidate as CatalogKey];
          if (typeof english === 'string') return interpolate(english, merged);
        }
        return key;
      },
      fit: (key, values) => {
        if (locale === 'en') return 1;
        const english = EN[key];
        if (typeof english !== 'string' || english.length === 0) return 1;
        const translated = interpolate(lookup(locale, key), values);
        if (translated.length <= english.length) return 1;
        return Math.max(MIN_FIT, english.length / translated.length);
      },
      at: (key, values) => {
        const text = t(key, values);
        const english = EN[key];
        const ratio =
          locale === 'en' || typeof english !== 'string' || text.length <= english.length
            ? 1
            : Math.max(MIN_FIT, english.length / text.length);
        // A custom property rather than a font-size: the stylesheet keeps
        // owning the size (it is in cqw and has to stay responsive), and this
        // only multiplies it. `var(--fit, 1)` means a slot nobody has wired up
        // behaves exactly as before.
        return { children: text, style: { '--fit': String(ratio) } };
      },
      number: (input, options) =>
        options ? new Intl.NumberFormat(tag, options).format(input) : numberFormat.format(input),
      date: (input, options) =>
        new Intl.DateTimeFormat(tag, options ?? { dateStyle: 'medium' }).format(input),
    };
    // `loaded` is in the dependency list on purpose: it is what rebuilds the
    // translator once a fetched catalogue is in the map.
  }, [locale, setLocale, loaded]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/**
 * The hook every component uses.
 *
 * Throws outside the provider rather than silently returning English: a screen
 * that renders untranslated because someone forgot a provider is exactly the
 * failure this system exists to prevent.
 */
export function useT(): Translator {
  const value = useContext(I18nContext);
  if (!value) throw new Error('useT must be used inside <I18nProvider>');
  return value;
}

export { loadCatalog as preloadLocale };
export type { Catalog, CatalogKey, LocaleCatalog, PluralBase };
